<template>
    <div class="zap-ms-wrap" ref="wrap">
        <div class="zap-ms-trigger" :class="{ open, disabled: readonly }" @click="onTriggerClick">
            <div class="zap-ms-display" :class="{ placeholder: value.length === 0 }">
                {{ displayText }}
            </div>
            <div class="zap-ms-chevron">
                <v-icon size="16">mdi-chevron-down</v-icon>
            </div>
        </div>
        <div class="zap-ms-dropdown" :class="{ open }">
            <div class="zap-ms-search">
                <input ref="search" type="text" :placeholder="$t('common.search')" v-model="query" />
            </div>
            <div class="zap-ms-options">
                <div v-for="item in filteredItems" :key="item.objectID" class="zap-ms-option"
                    :class="{ selected: isSelected(item) }" @click.stop="toggle(item)">
                    <div class="zap-ms-checkbox">
                        <v-icon v-if="isSelected(item)" size="10" color="#ffffff">mdi-check</v-icon>
                    </div>
                    <div class="zap-ms-av" :style="gradient(item)">{{ initials(item) }}</div>
                    <span class="zap-ms-option-name">{{ item.name }}</span>
                </div>
                <div v-if="filteredItems.length === 0" class="zap-ms-empty">
                    {{ $t('common.no_results') }}
                </div>
            </div>
            <div class="zap-ms-footer">
                <span class="zap-ms-footer-count">
                    <span>{{ value.length }}</span> / {{ maxSelected }}
                </span>
                <button class="btn-zap-start-inline" :disabled="value.length === 0" @click.stop="onStart">
                    <v-icon size="13">mdi-flash</v-icon>
                    {{ $t('common.start') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { getInitials, getAvatarGradient } from '@/utils/Common'

export default {
    name: 'ZappingStudentPicker',
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        value: {
            type: Array,
            default: () => [],
        },
        maxSelected: {
            type: Number,
            default: 20,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            open: false,
            query: '',
        }
    },
    computed: {
        filteredItems(vm = this) {
            const q = vm.query.trim().toLowerCase()
            const items = q ? vm.items.filter(item => (item.name || '').toLowerCase().includes(q)) : vm.items
            return [...items].sort((a, b) =>
                (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
            )
        },
        displayText(vm = this) {
            if (vm.value.length === 0) return vm.$t('common.select_placeholder')
            return vm.value.map(s => s.name).join(', ')
        },
    },
    mounted() {
        document.addEventListener('click', this.onOutsideClick)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onOutsideClick)
    },
    methods: {
        initials(item) {
            return getInitials(item.name)
        },
        gradient(item) {
            const g = getAvatarGradient(item.objectID || item.name || '')
            return { background: g.background, color: g.color }
        },
        isSelected(item) {
            return this.value.some(s => s.objectID === item.objectID)
        },
        onTriggerClick() {
            const vm = this
            if (vm.readonly) return
            vm.open = !vm.open
            if (vm.open) vm.$nextTick(() => vm.$refs.search && vm.$refs.search.focus())
        },
        toggle(item) {
            const vm = this
            let selected = [...vm.value]
            const idx = selected.findIndex(s => s.objectID === item.objectID)
            if (idx >= 0) {
                selected.splice(idx, 1)
            } else {
                if (selected.length >= vm.maxSelected) return
                selected.push(item)
            }
            vm.$emit('change', selected)
        },
        onStart() {
            const vm = this
            if (vm.value.length === 0) return
            vm.open = false
            vm.$emit('start')
        },
        onOutsideClick(e) {
            if (this.$refs.wrap && !this.$refs.wrap.contains(e.target)) this.open = false
        },
    },
    watch: {
        readonly(val) {
            if (val) this.open = false
        },
    },
}
</script>

<style scoped>
.zap-ms-wrap {
    position: relative;
    width: 100%;
}

.zap-ms-trigger {
    width: 100%;
    background: var(--v-field-base);
    border: 1px solid var(--v-accent-base);
    border-radius: 8px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 44px;
    cursor: pointer;
    transition: border-color 0.15s;
}

.zap-ms-trigger.open {
    border-radius: 8px 8px 0 0;
}

.zap-ms-trigger.disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.zap-ms-display {
    flex: 1;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.zap-ms-display.placeholder {
    color: var(--v-txtMuted-base);
}

.zap-ms-chevron {
    flex-shrink: 0;
    transition: transform 0.2s;
}

.zap-ms-chevron .v-icon {
    color: var(--v-txtMuted-base);
}

.zap-ms-trigger.open .zap-ms-chevron {
    transform: rotate(180deg);
}

.zap-ms-dropdown {
    display: none;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--v-field-base);
    border: 1px solid var(--v-accent-base);
    border-top: none;
    border-radius: 0 0 8px 8px;
    max-height: 300px;
    overflow: hidden;
    z-index: 30;
}

.zap-ms-dropdown.open {
    display: flex;
}

.zap-ms-search {
    padding: 8px 12px;
    border-bottom: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.zap-ms-search input {
    width: 100%;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline2-base);
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12px;
    color: var(--v-txtPrimary-base);
    font-family: inherit;
    outline: none;
}

.zap-ms-search input:focus {
    border-color: var(--v-accent-base);
}

.zap-ms-options {
    overflow-y: auto;
    flex: 1;
}

.zap-ms-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    cursor: pointer;
    font-size: 13px;
    color: var(--v-txtSecondary-base);
    transition: background 0.1s;
    border-bottom: 1px solid var(--v-outline-base);
}

.zap-ms-option:hover {
    background: rgba(51, 153, 153, 0.07);
    color: var(--v-txtPrimary-base);
}

.zap-ms-option.selected {
    color: var(--v-secondary-base);
}

.zap-ms-option-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.zap-ms-checkbox {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 1.5px solid var(--v-outline2-base);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.12s;
}

.zap-ms-option.selected .zap-ms-checkbox {
    background: var(--v-accent-base);
    border-color: var(--v-accent-base);
}

.zap-ms-av {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    font-size: 9px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.zap-ms-empty {
    padding: 18px 14px;
    text-align: center;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.zap-ms-footer {
    flex-shrink: 0;
    background: var(--v-field-base);
    border-top: 1px solid var(--v-outline-base);
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.zap-ms-footer-count {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.zap-ms-footer-count span {
    color: var(--v-secondary-base);
    font-weight: 600;
}

.btn-zap-start-inline {
    display: flex;
    align-items: center;
    gap: 6px;
    background: var(--brand-gradient);
    color: #0C0E12;
    font-weight: 700;
    font-family: inherit;
    font-size: 13px;
    border: none;
    border-radius: 8px;
    padding: 8px 18px;
    cursor: pointer;
    transition: opacity 0.15s;
}

.btn-zap-start-inline .v-icon {
    color: #0C0E12 !important;
}

.btn-zap-start-inline:disabled {
    opacity: 0.35;
    pointer-events: none;
}
</style>
